import React, { useState } from "react";
import Layout from "@/components/Layout";
import {
  HelpCircle, Sparkles, Camera, Users, ShoppingBag, CreditCard,
  Lightbulb, ChevronDown, Mail, CheckCircle2, Microscope,
} from "lucide-react";

const SECTIONS = [
  {
    id: "cosa-fa",
    icon: Sparkles,
    title: "Cosa fa il programma",
    intro:
      "Questo software ti permette di analizzare il cuoio capelluto dei clienti, salvare lo storico e monitorare eventuali trattamenti e vendite prodotti.",
  },
  {
    id: "come-analisi",
    icon: Camera,
    title: "Come fare un'analisi",
    steps: [
      "Collega la tricocamera al telefono / iPad / computer.",
      "Scatta le foto del cuoio capelluto.",
      "Carica le immagini nel sistema.",
      "Avvia l'analisi automatica.",
      "Visualizza il risultato e salva lo storico cliente.",
    ],
  },
  {
    id: "tricocamera",
    icon: Microscope,
    title: "Requisiti tricocamera per analisi AI TricoProtocol™",
    intro:
      "Per ottenere un'analisi affidabile, la tricocamera deve rispettare alcuni requisiti tecnici minimi. Questi standard servono a garantire accuratezza, coerenza dei risultati e uniformità tra saloni diversi.",
    subsections: [
      {
        title: "1. Tipologia dispositivo",
        body: "La tricocamera deve essere preferibilmente Wi-Fi, per consentire:",
        bullets: [
          "connessione diretta con smartphone o tablet",
          "acquisizione immagini in tempo reale",
          "facilità di utilizzo in salone, senza cavi",
        ],
      },
      {
        title: "2. Zoom e ingrandimento",
        body: "La tricocamera deve supportare almeno due livelli di ingrandimento:",
        bullets: [
          "50x — analisi generale della cute (minimo richiesto)",
          "200x — analisi dettagliata di bulbo, ostio follicolare, sebo e desquamazione (ingrandimento ottimale)",
        ],
      },
      {
        title: "3. Qualità immagine",
        body: "Risoluzione minima consigliata: HD (1280×720). Preferibile: Full HD (1920×1080). La fotocamera deve garantire:",
        bullets: [
          "buona messa a fuoco automatica o manuale",
          "assenza di rumore e sfocature",
          "colori realistici, senza dominanti cromatiche",
        ],
      },
      {
        title: "4. Illuminazione",
        body: "L'illuminazione integrata è fondamentale per la qualità dell'immagine:",
        bullets: [
          "LED integrati regolabili in intensità",
          "luce neutra — evitare dominanti troppo fredde o troppo calde",
          "illuminazione uniforme su tutta l'area inquadrata",
        ],
      },
      {
        title: "5. Compatibilità",
        body: "La tricocamera deve essere compatibile con:",
        bullets: [
          "iOS (iPhone, iPad)",
          "Android (smartphone e tablet)",
          "possibilità di salvare o esportare le immagini in formato JPG/PNG",
        ],
      },
      {
        title: "6. Stabilità e utilizzo",
        bullets: [
          "consigliato un supporto stabile o mano ferma durante lo scatto",
          "evitare movimenti durante l'acquisizione per non compromettere la nitidezza",
          "posizionamento corretto sulla cute, senza eccessiva pressione",
        ],
      },
      {
        title: "7. Note operative",
        bullets: [
          "evitare immagini sfocate o sovraesposte",
          "pulire la lente prima di ogni utilizzo",
          "effettuare almeno 3 scatti per zona: frontale, laterale e vertice",
        ],
      },
    ],
    footer:
      "Rispettando questi requisiti, l'analisi AI risulta più accurata, riduce errori di interpretazione e garantisce risultati uniformi tra saloni differenti. È la base per un protocollo cosmetico davvero professionale.",
    footerTone: "info",
  },
  {
    id: "clienti",
    icon: Users,
    title: "Clienti",
    intro: "Ogni cliente ha una scheda con:",
    bullets: [
      "analisi salvate",
      "storico completo delle visite",
      "eventuali prodotti venduti",
    ],
    footer: "I clienti evidenziati in verde sono quelli su cui hai venduto almeno un prodotto.",
    footerTone: "success",
  },
  {
    id: "vendita",
    icon: ShoppingBag,
    title: "Vendita prodotti",
    bullets: [
      "Dopo una diagnosi, puoi associare un prodotto al cliente flaggando la checkbox \"Venduto\" sul prodotto della routine domiciliare.",
      "Questo serve per tracciare le vendite e monitorare i risultati nel tempo.",
      "I prodotti del Protocollo Salone sono servizi professionali: non si vendono, si eseguono.",
    ],
    footer: "Quando assegni un prodotto, il cliente diventa \"attivo\" ed è evidenziato in verde nella dashboard.",
    footerTone: "success",
  },
  {
    id: "piani",
    icon: CreditCard,
    title: "Piani",
    bullets: [
      "Free: 5 analisi totali di prova, senza download PDF.",
      "Basic: 50 analisi al mese + accesso completo allo storico + PDF professionale.",
      "Pro: analisi illimitate (fair-use) + supporto dedicato.",
    ],
    footer: "Se raggiungi il limite, passa a un piano superiore dalla sezione Piani nel menu.",
    footerTone: "info",
  },
  {
    id: "consigli",
    icon: Lightbulb,
    title: "Consigli pratici",
    bullets: [
      "Usa sempre foto nitide e ben illuminate.",
      "Mantieni sempre lo stesso punto di analisi nel tempo per confrontare l'evoluzione.",
      "Registra i prodotti venduti per ogni cliente: così puoi valutare cosa funziona davvero.",
      "Rivedi i risultati nel tempo per fidelizzare la clientela.",
    ],
  },
];

function AccordionItem({ section, open, onToggle, index }) {
  const Icon = section.icon;
  return (
    <div
      className={`rounded-2xl border transition-all ${
        open ? "border-[#C5A059] bg-white shadow-[0_4px_24px_rgba(197,160,89,0.08)]" : "border-[#E5E5E0] bg-white"
      }`}
      data-testid={`help-section-${section.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-6 text-left"
        data-testid={`help-toggle-${section.id}`}
      >
        <div className="w-12 h-12 rounded-xl bg-[#EAE5DF] flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-[#C5A059]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-1">
            Passo {index + 1}
          </p>
          <h3 className="font-serif-display text-2xl text-[#1A1A1A] leading-tight">
            {section.title}
          </h3>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-[#737373] transition-transform shrink-0 ${
            open ? "rotate-180 text-[#C5A059]" : ""
          }`}
        />
      </button>

      {open && (
        <div className="px-6 pb-7 pl-24 animate-fade-up">
          {section.intro && (
            <p className="text-[#1A1A1A] leading-relaxed mb-4">{section.intro}</p>
          )}
          {section.steps && (
            <ol className="space-y-3">
              {section.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#C5A059] text-white text-xs font-semibold shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-[#1A1A1A] leading-relaxed pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          )}
          {section.bullets && (
            <ul className="space-y-2.5">
              {section.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-[#C5A059] mt-1 shrink-0" />
                  <span className="text-[#1A1A1A] leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          )}
          {section.subsections && (
            <div className="space-y-6" data-testid={`help-subsections-${section.id}`}>
              {section.subsections.map((sub, si) => (
                <div
                  key={si}
                  className="rounded-xl bg-[#FAF8F4] border border-[#E5E5E0] p-5"
                >
                  <h4 className="font-serif-display text-lg text-[#1A1A1A] mb-2">
                    {sub.title}
                  </h4>
                  {sub.body && (
                    <p className="text-sm text-[#404040] leading-relaxed mb-3">{sub.body}</p>
                  )}
                  {sub.bullets && (
                    <ul className="space-y-2">
                      {sub.bullets.map((b, bi) => (
                        <li key={bi} className="flex items-start gap-3">
                          <span className="text-[#C5A059] mt-1">›</span>
                          <span className="text-[15px] text-[#1A1A1A] leading-relaxed">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
          {section.footer && (
            <div
              className={`mt-5 rounded-xl px-4 py-3 text-sm leading-relaxed border ${
                section.footerTone === "success"
                  ? "bg-emerald-50/60 border-emerald-200 text-emerald-900"
                  : "bg-[#FAF8F4] border-[#E5E5E0] text-[#1A1A1A]"
              }`}
            >
              👉 {section.footer}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Help() {
  const [openId, setOpenId] = useState("cosa-fa");

  return (
    <Layout>
      <div className="mb-10 animate-fade-up">
        <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">Guida rapida</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] mb-3">
          Come funziona AI TricoProtocol
        </h1>
        <p className="text-[#737373] max-w-2xl leading-relaxed">
          Una guida veloce per iniziare, anche se non hai esperienza tecnica.
          Dalla prima analisi alla gestione dei clienti e delle vendite, in pochi passi.
        </p>
      </div>

      <div className="space-y-4 max-w-3xl" data-testid="help-accordion">
        {SECTIONS.map((s, i) => (
          <AccordionItem
            key={s.id}
            section={s}
            index={i}
            open={openId === s.id}
            onToggle={() => setOpenId(openId === s.id ? null : s.id)}
          />
        ))}
      </div>

      {/* Support contact */}
      <div
        className="mt-10 rounded-2xl border border-[#C5A059] bg-gradient-to-br from-[#1A1A1A] to-[#2A2A2A] text-white p-8 max-w-3xl"
        data-testid="help-support-card"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#C5A059]/20 flex items-center justify-center shrink-0">
            <HelpCircle className="w-5 h-5 text-[#C5A059]" />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-2">
              Hai bisogno di aiuto?
            </p>
            <h3 className="font-serif-display text-2xl mb-2">
              Il nostro team è qui per te
            </h3>
            <p className="text-white/70 leading-relaxed mb-4">
              Non trovi la risposta che cerchi? Scrivici e ti rispondiamo il prima possibile.
            </p>
            <a
              href="mailto:supporto@aitricoprotocol.com"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#C5A059] hover:bg-white hover:text-[#1A1A1A] text-white font-medium transition-all"
              data-testid="help-support-email"
            >
              <Mail className="w-4 h-4" />
              supporto@aitricoprotocol.com
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
}
